import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export type SendToAccountingVariables = {
	poNumber: string;
	message?: string;
};

export const postSendAccounting = async ({
	poNumber,
	message,
}: SendToAccountingVariables) => {
	const trimmedMessage = message?.trim();
	const resp = await postAndValidate(
		apiRoutes.POSTSENDACCOUNTING.replace(":poNumber", poNumber),
		{
			value: true,
			acknowledgeBillingZeroWarning: true,
			acknowledgeCustomerBillZeroWarning: true,
			acknowledgePayTruckZeroWarning: true,
			...(trimmedMessage ? { message: trimmedMessage } : {}),
		},
		z.unknown(),
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostSendAccounting = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();
	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		unknown,
		Error,
		SendToAccountingVariables
	>({
		mutationKey: ["sendAccounting"],
		mutationFn: postSendAccounting,
		// Awaiting the refetch keeps the mutation pending until the shipment
		// reflects its accounting status lock, so the page can't be interacted
		// with in between. A failed refetch is swallowed by invalidateQueries.
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess("Sent to accounting.");
		},
		onError: (err) => {
			showError(
				err instanceof Error
					? err.message
					: "Failed to send to accounting. Please try again.",
			);
		},
	});

	return {
		sendAccounting: mutate,
		sendAccountingAsync: mutateAsync,
		isSendingAccounting: isPending,
		error,
		data,
		resetError: reset,
	};
};
